import React, { useMemo, useState } from 'react';

import { Chip, Link, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { type ExperimentalFeatureKey, experimentalFeatures } from '@luxuryescapes/contract-svc-promo';

interface Props {
  featureUrls: Readonly<Partial<Record<ExperimentalFeatureKey, string>>>;
}

type ExperimentalFeature = (typeof experimentalFeatures)[number];

function matchesSearch(feature: ExperimentalFeature, searchText: string) {
  const normalizedSearchText = searchText.trim().toLowerCase();

  if (!normalizedSearchText) {
    return true;
  }

  return [
    feature.key,
    feature.displayName,
    feature.description,
    feature.experimentalStatus,
    feature.currentStatusHeadline,
    feature.isFunctionallyComplete ? 'functionally complete' : '',
  ].some((value) => value.toLowerCase().includes(normalizedSearchText));
}

export default function PromoExperimentalFeatures({ featureUrls }: Props) {
  const [searchText, setSearchText] = useState('');
  const features = useMemo(
    () => experimentalFeatures.filter((feature) => matchesSearch(feature, searchText)),
    [searchText],
  );
  const columns = useMemo<Array<GridColDef<ExperimentalFeature>>>(
    () => [
      {
        field: 'displayName',
        headerName: 'Feature',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 280,
      },
      {
        field: 'currentStatusHeadline',
        headerName: 'Current rollout',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'isOnlyTest',
        headerName: 'Test only',
        minWidth: 110,
        renderCell: (params) =>
          params.row.isOnlyTest ? <Chip label="Test only" size="small" color="warning" /> : null,
      },
      {
        field: 'isFunctionallyComplete',
        headerName: 'Complete',
        minWidth: 120,
        renderCell: (params) =>
          params.row.isFunctionallyComplete ? <Chip label="Complete" size="small" color="success" /> : null,
      },
      {
        field: 'openFeature',
        headerName: 'Open',
        sortable: false,
        filterable: false,
        minWidth: 120,
        renderCell: (params) => {
          const featureUrl = featureUrls[params.row.key];

          return featureUrl ? <Link href={featureUrl}>Open feature</Link> : null;
        },
      },
    ],
    [featureUrls],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Features that have not reached general availability.
      </Typography>

      <TextField
        label="Search experimental features"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
      />

      <DataGrid
        aria-label="Experimental promo features"
        columns={columns}
        rows={features}
        getRowId={(feature) => feature.key}
        getRowHeight={() => 'auto'}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        sx={{ '.MuiDataGrid-cell': { alignItems: 'center', py: 1 } }}
      />
    </Stack>
  );
}
